import type { AppState } from './default-state';
import { resolveCollisions } from './collisions';

/**
 * The simulation-clock channel (AD-2). The clock is a SEPARATE injected input,
 * not a member of the closed command union: user commands go through `dispatch`,
 * the injected clock goes through `store.tick(dt)`, and both keep reducers pure
 * and free of any `Date`/wall-clock read.
 *
 * Unit boundary (AD-5): playback speed is expressed in simulated-days per real
 * second and converted to a `dt` in SI seconds exactly once, HERE. The day
 * literal `SECONDS_PER_DAY` lives in `state`, never in `core` (a day literal in
 * `core` violates AD-5), so `core` never sees sim-speed.
 */

/** Seconds in one day. The single day literal, kept out of `core` (AD-5). */
export const SECONDS_PER_DAY = 86400;

/**
 * Pure time advance: returns a new state with the simulation clock moved forward
 * by `dtSeconds` SI seconds. Replaces the state object so reactive subscribers
 * re-render (AD-2). Never reads `Date`.
 *
 * Paused gate (Story 1.5, AC 3): when `state.paused` the clock does not advance;
 * the ticker still fires each frame but this is a no-op, so `time` holds and
 * rendered positions freeze. Making the gate a pure branch keeps "paused implies
 * time does not advance" a headless unit test. Because this early-returns while
 * paused, collisions are only detected while playing (Story 4.1): the static paused
 * default never spuriously collides.
 *
 * Collision resolution (Story 4.1, AD-2): the WHOLE clock step is one pure function.
 * After moving the clock forward, `resolveCollisions` sub-samples `[prevTime,
 * newTime]` and flags any active non-central orbiting pair that came into contact.
 * A collision is therefore a consequence of the clock advancing, NOT a user command:
 * no verb is added to the closed command union and `store.tick`/`store.dispatch`
 * stay unchanged.
 */
export function advanceTime(state: AppState, dtSeconds: number): AppState {
  if (state.paused) return state;
  const prevTime = state.time;
  const advanced = { ...state, time: prevTime + dtSeconds };
  return resolveCollisions(advanced, prevTime, advanced.time);
}

/**
 * Convert a real-time frame delta and the playback speed to a simulation `dt` in
 * SI seconds: `realElapsedSeconds * speedDaysPerSecond * speedScale * SECONDS_PER_DAY`.
 * This is the single place playback speed becomes `dt`; `core` never sees sim-speed.
 *
 * `speedScale` (default `1`) is the central-body speed scale (`selectSpeedScale`):
 * `speedDaysPerSecond` is a Sun-normalized base, and this factor converts it to the
 * actual warp so orbits run at the same visual rate around any centre. It cancels the
 * central mass in mean-anomaly space, so the sub-step count stays bounded exactly as
 * at the Sun. `1` (the Sun) leaves the conversion byte-identical.
 */
export function simSecondsForFrame(
  realElapsedSeconds: number,
  speedDaysPerSecond: number,
  speedScale = 1
): number {
  return realElapsedSeconds * speedDaysPerSecond * speedScale * SECONDS_PER_DAY;
}
